import React, { useState, useEffect, useRef, useCallback } from 'react';
import { EditableDataGrid } from '../components/common/EditableDataGrid';
import { Modal } from '../components/common/Modal';
import { PageSyncMeta } from '../components/sync/PageSyncMeta';
import { masterApi } from '../api/masterApi';
import { NisugataHistoryViewer } from '../components/nisugata/NisugataHistoryViewer';

// 製品種別フィルタの定義
const productTypeFilters = {
  all: { label: '全件', classcode0: null, classcode1: null, classcode2: null, isOther: false },
  jukuwaku: { label: '受枠', classcode0: 16, classcode1: null, classcode2: null, isOther: false },
  kago: { label: 'カゴ', classcode0: 19, classcode1: 183, classcode2: null, isOther: false },
  bolt: { label: 'ボルト', classcode0: 20, classcode1: 198, classcode2: 704, isOther: false },
  tekkin: { label: '鉄筋', classcode0: 19, classcode1: [188, 195], classcode2: null, isOther: false },
  other: { label: 'その他', classcode0: null, classcode1: null, classcode2: null, isOther: true },
};

const NisugataMasterPage = () => {
  const [masters, setMasters] = useState([]);
  const [loading, setLoading] = useState(false); // 初期値を false に変更
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [filter, setFilter] = useState({ keyword: '' });
  const [onlyNeedsReview, setOnlyNeedsReview] = useState(false);
  const [selectedProductType, setSelectedProductType] = useState('all'); // 選択中の製品種別
  const [selectedMaster, setSelectedMaster] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [acknowledgingCode, setAcknowledgingCode] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [historyData, setHistoryData] = useState({});
  const [loadingHistory, setLoadingHistory] = useState({});
  const offsetRef = useRef(0);
  const observerTarget = useRef(null);
  const observerRef = useRef(null);
  const loadingInitialRef = useRef(false); // 初期ロード実行中フラグ
  const [isInitialLoad, setIsInitialLoad] = useState(false); // 初回ロード中フラグ（useStateに変更）
  const mastersRef = useRef([]); // mastersの最新値を追跡
  const initialLoadCompletedRef = useRef(false); // 初回ロード完了フラグ
  
  // 最新の状態を参照するためのref
  const stateRef = useRef({ loadingMore: false, hasMore: true, loading: false });
  const filterRef = useRef(filter);
  const selectedProductTypeRef = useRef(selectedProductType);
  const onlyNeedsReviewRef = useRef(onlyNeedsReview);

  useEffect(() => {
    stateRef.current = { loadingMore, hasMore, loading };
  }, [loadingMore, hasMore, loading]);

  useEffect(() => {
    filterRef.current = filter;
  }, [filter]);

  useEffect(() => {
    selectedProductTypeRef.current = selectedProductType;
  }, [selectedProductType]);

  useEffect(() => {
    onlyNeedsReviewRef.current = onlyNeedsReview;
  }, [onlyNeedsReview]);

  // mastersの最新値をrefで追跡
  useEffect(() => {
    mastersRef.current = masters;
  }, [masters]);

  const loadInitialItems = useCallback(async () => {
    // 既に実行中の場合はスキップ（React Strict Mode の二重実行を防ぐ）
    if (loadingInitialRef.current) {
      return;
    }
    
    // 初回ロード中かどうかを判定（masters.length === 0 の時）
    const isInitial = mastersRef.current.length === 0;
    
    try {
      loadingInitialRef.current = true;
      setIsInitialLoad(isInitial); // 初回ロード中フラグを設定（useState）
      setLoading(true);
      offsetRef.current = 0;
      const productTypeFilter = productTypeFilters[selectedProductType];
      // 鉄筋の場合はOR条件なので、配列として処理
      const classcode1Value = Array.isArray(productTypeFilter.classcode1) 
        ? productTypeFilter.classcode1.join(',') 
        : productTypeFilter.classcode1;
      const data = await masterApi.getNisugataMasters(
        filter.keyword, 
        100, 
        0,
        productTypeFilter.classcode0,
        classcode1Value,
        productTypeFilter.classcode2,
        productTypeFilter.isOther,
        onlyNeedsReview
      );
      
      // データ取得後、初回ロードフラグをリセット（setMastersの前に実行）
      if (isInitial) {
        setIsInitialLoad(false);
      }
      
      setMasters(data);
      offsetRef.current = data.length;
      // hasMoreの判定を修正：取得したデータがlimit（100件）と等しい場合のみtrue
      setHasMore(data.length >= 100);
      // 初回ロード完了フラグを設定
      if (isInitial) {
        initialLoadCompletedRef.current = true;
        // 初回ロード完了後、少し待ってからフラグをリセット（IntersectionObserverの発火を防ぐため）
        setTimeout(() => {
          initialLoadCompletedRef.current = false;
        }, 500);
      }
    } catch (error) {
      console.error('一覧取得エラー:', error);
      alert('一覧の取得に失敗しました');
    } finally {
      loadingInitialRef.current = false;
      setLoading(false);
    }
  }, [filter.keyword, selectedProductType, onlyNeedsReview]);

  useEffect(() => {
    loadInitialItems();
  }, [loadInitialItems]);

  const loadMoreItems = async () => {
    const currentState = stateRef.current;
    
    // 初回ロード中または初回ロード完了直後、または既にロード中、またはデータがない場合はスキップ
    if (loadingInitialRef.current || initialLoadCompletedRef.current || currentState.loadingMore || !currentState.hasMore || currentState.loading) {
      return;
    }

    try {
      const currentOffset = offsetRef.current;
      const currentFilter = filterRef.current;
      const currentSelectedProductType = selectedProductTypeRef.current;
      setLoadingMore(true);
      const productTypeFilter = productTypeFilters[currentSelectedProductType];
      // 鉄筋の場合はOR条件なので、配列として処理
      const classcode1Value = Array.isArray(productTypeFilter.classcode1) 
        ? productTypeFilter.classcode1.join(',') 
        : productTypeFilter.classcode1;
      const data = await masterApi.getNisugataMasters(
        currentFilter.keyword, 
        100, 
        currentOffset,
        productTypeFilter.classcode0,
        classcode1Value,
        productTypeFilter.classcode2,
        productTypeFilter.isOther,
        onlyNeedsReviewRef.current
      );
      setMasters(prevItems => {
        return [...prevItems, ...data];
      });
      offsetRef.current = currentOffset + data.length;
      setHasMore(data.length === 100);
    } catch (error) {
      console.error('追加データ取得エラー:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    // masters.lengthが0の場合は、observerTargetが存在しないのでスキップ
    if (masters.length === 0) {
      return;
    }
    
    // オブザーバーは一度だけ作成
    if (!observerRef.current) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry) return;

          const currentState = stateRef.current;
          
          // 初回ロード中または初回ロード完了直後はloadMoreItemsを呼ばない
          if (entry.isIntersecting && currentState.hasMore && !currentState.loadingMore && !currentState.loading && !loadingInitialRef.current && !initialLoadCompletedRef.current) {
            loadMoreItems();
          }
        },
        { 
          threshold: 0,
          rootMargin: '500px'
        }
      );
    }

    const observer = observerRef.current;

    // observerTargetが存在するまで待機してからオブザーバーを設定
    const setupObserver = () => {
      const target = observerTarget.current;
      if (target) {
        // 既に監視中の場合は一度解除してから再設定（確実に監視を開始するため）
        const isObserving = target.getAttribute('data-observing') === 'true';
        if (isObserving) {
          observer.unobserve(target);
          target.removeAttribute('data-observing');
        }
        // 少し待ってから再設定（DOM更新を待つ）
        const timeoutId = setTimeout(() => {
          const currentTarget = observerTarget.current;
          if (currentTarget && !currentTarget.getAttribute('data-observing')) {
            observer.observe(currentTarget);
            currentTarget.setAttribute('data-observing', 'true');
          }
        }, 50);
        return { timeoutId };
      }
      return null;
    };

    // 即座にチェック
    let setupResult = setupObserver();
    let timeoutId1 = null;
    let timeoutId2 = null;
    let timeoutId3 = null;

    if (!setupResult) {
      // 少し待ってから再試行（DOMが更新されるのを待つ）
      timeoutId1 = setTimeout(() => {
        setupResult = setupObserver();
        if (!setupResult) {
          // もう一度試行
          timeoutId2 = setTimeout(() => {
            setupResult = setupObserver();
            if (!setupResult) {
              // 最後の試行
              timeoutId3 = setTimeout(() => {
                setupObserver();
              }, 300);
            }
          }, 300);
        }
      }, 300);
    }

    return () => {
      if (setupResult?.timeoutId) clearTimeout(setupResult.timeoutId);
      if (timeoutId1) clearTimeout(timeoutId1);
      if (timeoutId2) clearTimeout(timeoutId2);
      if (timeoutId3) clearTimeout(timeoutId3);
      const target = observerTarget.current;
      if (target && target.hasAttribute('data-observing')) {
        observer.unobserve(target);
        target.removeAttribute('data-observing');
      }
    };
  }, [hasMore, masters.length]); // hasMoreとmasters.lengthを依存配列に追加

  const handleCellChange = (productCode, field, newValue, oldValue) => {
    // セル変更時の処理（必要に応じて）
  };

  const handleSave = async (productCode, updateData) => {
    try {
      await masterApi.updateNisugataMaster(productCode, updateData);
      await loadInitialItems();
      return true;
    } catch (error) {
      console.error('保存エラー:', error);
      throw error;
    }
  };

  const handleRowClick = (row) => {
    setSelectedMaster(row);
  };

  const handleRowExpand = (productCode) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(productCode)) {
      // 既に開いている場合は閉じる
      newExpandedRows.delete(productCode);
    } else {
      // 新しい行を開く場合は、既存の展開をすべて閉じてから開く
      newExpandedRows.clear();
      newExpandedRows.add(productCode);
    }
    setExpandedRows(newExpandedRows);
  };

  const handleHistoryRequest = async (productCode) => {
    // 既に取得済みの場合はスキップ
    if (historyData[productCode] || loadingHistory[productCode]) {
      return;
    }

    try {
      setLoadingHistory(prev => ({ ...prev, [productCode]: true }));
      // 履歴取得件数を50件に制限（パフォーマンス改善）
      const data = await masterApi.getNisugataMasterHistory(productCode, undefined, undefined, undefined, 50, 0);
      setHistoryData(prev => ({ ...prev, [productCode]: data }));
    } catch (error) {
      console.error('履歴取得エラー:', error);
      alert('履歴の取得に失敗しました');
    } finally {
      setLoadingHistory(prev => ({ ...prev, [productCode]: false }));
    }
  };

  const handleHistoryClick = (e) => {
    e.stopPropagation();
    setHistoryOpen(true);
  };

  const handleImport = async () => {
    if (!importFile) {
      alert('ファイルを選択してください');
      return;
    }

    try {
      setImporting(true);
      const result = await masterApi.importNisugataMasters(importFile);
      alert(`${result.updated_count}件のデータをインポートしました`);
      setImportModalOpen(false);
      setImportFile(null);
      await loadInitialItems();
    } catch (error) {
      console.error('インポートエラー:', error);
      alert('インポートに失敗しました: ' + (error.message || '不明なエラー'));
    } finally {
      setImporting(false);
    }
  };

  const handleRefresh = () => {
    loadInitialItems();
  };

  const handleAcknowledgeCoreSync = async (productCode) => {
    if (!productCode) {
      return;
    }
    try {
      setAcknowledgingCode(productCode);
      await masterApi.acknowledgeCoreSync(productCode);
      await loadInitialItems();
    } catch (error) {
      console.error('確認済み更新エラー:', error);
      alert('確認済みの更新に失敗しました');
    } finally {
      setAcknowledgingCode(null);
    }
  };

  const renderExpandedToolbar = (row) => {
    if (!row.needs_review) {
      return null;
    }
    const statusLabel = row.core_sync_status === 'new' ? '基幹新規' : '基幹更新';
    const isAcknowledging = acknowledgingCode === row.product_code;
    return (
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium text-slate-700">{statusLabel}</span>
        <button
          type="button"
          onClick={() => handleAcknowledgeCoreSync(row.product_code)}
          disabled={isAcknowledging}
          className="btn-toolbar shrink-0 bg-teal-800 text-white rounded-md hover:bg-teal-900 transition-colors text-sm font-medium disabled:bg-teal-500 disabled:text-teal-50 disabled:cursor-not-allowed disabled:hover:bg-teal-500"
        >
          {isAcknowledging ? '処理中...' : '確認済みにする'}
        </button>
      </div>
    );
  };

  const getRowClassName = (row) => {
    if (!row.needs_review) {
      return '';
    }
    if (row.core_sync_status === 'new') {
      return 'bg-amber-50 hover:bg-amber-100';
    }
    if (row.core_sync_status === 'updated') {
      return 'bg-sky-50 hover:bg-sky-100';
    }
    return 'bg-yellow-50 hover:bg-yellow-100';
  };

  const columns = [
    { header: '製品CD', field: 'product_disp', readonly: true, width: 120 },
    { header: '製品名', field: 'product_name', readonly: true, width: 200 },
    { header: '寸法', field: 'size', readonly: true, width: 120 },
    { header: '重量(kg)', field: 'weight', type: 'number', readonly: true, width: 100 },
    { header: 'メーカー入り数/箱', field: 'maker_qty_per_box', readonly: true, width: 140 },
    { header: '主工場CD', field: 'factory_code', readonly: true, width: 100 },
    { header: '主工場名', field: 'factory_name', readonly: true, width: 150 },
    { header: '主仕入先CD', field: 'supplier_code', readonly: true, width: 120 },
    { header: '主仕入先名', field: 'supplier_name', readonly: true, width: 180 },
    { header: '1PLの数量（情報）', field: 'pallet_qty_info', width: 180 },
    { header: '1PLの数量（数字）', field: 'pallet_qty', type: 'number', width: 140 },
    { header: '主な発送先', field: 'main_delivery_dest', width: 200 },
    { header: '特別仕様など', field: 'special_spec', width: 200 },
    { header: 'ボルト数/台', field: 'bolt_qty_per_unit', type: 'number', width: 120 },
  ];

  return (
    <div className="h-full flex flex-col bg-slate-50">
      <div className="p-6 border-b border-slate-200 bg-white compact:px-4 compact:py-3">
        <div className="page-header">
          <h1 className="page-title">荷姿管理</h1>
          <div className="page-header-actions">
            <PageSyncMeta groups={['master']} />
            <button
              type="button"
              onClick={handleRefresh}
              disabled={loading}
              className="flex items-center gap-2 btn-toolbar bg-slate-700 text-white rounded-md hover:bg-slate-800 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg
                className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              更新
            </button>
          </div>
        </div>
      </div>
      <div className="px-6 py-3 border-b border-slate-200 bg-white compact:px-4 compact:py-2">
        <div className="bg-white space-y-2">
          <form onSubmit={(e) => { e.preventDefault(); loadInitialItems(); }} className="flex flex-wrap gap-3 items-center">
            <input
              type="text"
              value={filter.keyword}
              onChange={(e) => setFilter({ keyword: e.target.value })}
              placeholder="製品CD / 製品名 / 寸法で検索"
              className="flex-1 px-4 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500 text-slate-900"
            />
            <button
              type="submit"
              className="btn-toolbar bg-slate-700 text-white rounded-md hover:bg-slate-800 transition-colors font-medium text-sm"
            >
              検索
            </button>
            <button
              type="button"
              onClick={() => { setFilter({ keyword: '' }); loadInitialItems(); }}
              className="btn-toolbar bg-slate-100 text-slate-700 rounded-md hover:bg-slate-200 transition-colors font-medium text-sm"
            >
              クリア
            </button>
            <button
              type="button"
              onClick={handleHistoryClick}
              className="btn-toolbar bg-slate-600 text-white rounded-md hover:bg-slate-700 transition-colors text-sm"
            >
              履歴表示
            </button>
            {/* CSVインポート（現在未使用のため非表示） */}
          </form>
          <div className="filter-toolbar flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-slate-700 shrink-0">種別:</span>
            {Object.entries(productTypeFilters).map(([key, filter]) => (
              <button
                key={key}
                type="button"
                onClick={() => {
                  setSelectedProductType(key);
                }}
                className={`px-3 py-1.5 rounded-md transition-colors font-medium text-sm ${
                  selectedProductType === key
                    ? 'bg-slate-700 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {filter.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setOnlyNeedsReview((prev) => !prev)}
              className={`px-3 py-1.5 rounded-md transition-colors font-medium text-sm ${
                onlyNeedsReview
                  ? 'bg-slate-700 text-white hover:bg-slate-800'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              基幹変更あり
            </button>
            <span className="ml-auto flex flex-wrap items-center gap-3 text-xs text-slate-600 shrink-0">
              <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-50 border border-amber-200" /> 基幹新規</span>
              <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-sky-50 border border-sky-200" /> 基幹更新</span>
            </span>
          </div>
        </div>
      </div>
      <div className="flex-1 flex flex-col overflow-hidden px-6 pt-4 pb-2">
        <div className="flex-1 bg-white rounded-lg shadow-sm border border-slate-200 flex flex-col overflow-hidden mb-2">
          <EditableDataGrid
            columns={columns}
            data={masters}
            onCellChange={handleCellChange}
            onSave={handleSave}
            selectedRow={selectedMaster}
            onRowClick={handleRowClick}
            expandedRows={expandedRows}
            onRowExpand={handleRowExpand}
            historyData={historyData}
            onHistoryRequest={handleHistoryRequest}
            loading={loading}
            observerTarget={hasMore && masters.length > 0 ? observerTarget : null}
            getRowClassName={getRowClassName}
            renderExpandedToolbar={renderExpandedToolbar}
          />
          {!hasMore && masters.length > 0 && !loading && (
            <div className="text-center py-3 text-slate-500 text-sm bg-slate-50 border-t border-slate-200 rounded-b-lg flex-shrink-0">すべてのデータを表示しました</div>
          )}
        </div>
      </div>

      <NisugataHistoryViewer
        isOpen={historyOpen}
        onClose={() => setHistoryOpen(false)}
        productCode={selectedMaster?.product_code}
      />

      <Modal
        isOpen={importModalOpen}
        onClose={() => {
          setImportModalOpen(false);
          setImportFile(null);
        }}
        title="CSVインポート"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              CSVファイルを選択
            </label>
            <input
              type="file"
              accept=".csv"
              onChange={(e) => setImportFile(e.target.files[0])}
              className="w-full px-3 py-2 border border-slate-300 rounded-md"
            />
          </div>
          <div className="flex gap-3 pt-4">
            <button
              onClick={handleImport}
              disabled={!importFile || importing}
              className="flex-1 px-4 py-2 bg-slate-700 text-white rounded-md hover:bg-slate-800 disabled:opacity-50"
            >
              {importing ? 'インポート中...' : 'インポート'}
            </button>
            <button
              onClick={() => {
                setImportModalOpen(false);
                setImportFile(null);
              }}
              className="px-4 py-2 bg-slate-200 text-slate-700 rounded-md hover:bg-slate-300"
            >
              キャンセル
            </button>
          </div>
        </div>
      </Modal>

      {loadingMore && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
          <div className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg shadow-lg">
            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-sm font-medium">追加データを読み込み中...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default NisugataMasterPage;
